import React, { useEffect, useState } from "react";
import { Box, Skeleton, Toolbar, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { getTransactions } from "../../../services/transactionService";
import TableRowsLoader from "../../../components/common/Loader/tableLoader";
import TransactionDrawer from "../../../components/transactionDrawer/TransactionDrawer";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { formatDollar } from "../../../utils/utils";
import {
  GridFilterModel
} from "@mui/x-data-grid";
import {
  setTransactionsDetailsState,
  updateTimeframe,
} from "../../../redux/features/transactionsSlice";
import "./styles.scss";
import { AccountType, TransactionDataType, TransactionDetailsType } from "../../../types/genericType";
import { getAccountById } from "../../../services/accountService";
import { getTransactionsFromData } from "../../../utils/utils";
// import { formatDate } from "../../../utils/dateFormat";
import TransactionTable from "../../../components/transactionTable/TransactionTable";
import TableFilters from "../../../components/transactionTable/TableFilters";

const TransactionsHistory = () => {
  const { transactionDetails, startFilterDate, endFilterDate } = useSelector(
    (state: RootState) => state.transactionDetailsState
  );
  let [searchParams, setSearchParams] = useSearchParams();
  const account = searchParams?.get("account");
  const client = searchParams?.get("client");
  const distributionId = searchParams?.get("distributionId");

  const { role } = useSelector((state: RootState) => state.userDetails);
  const auditorView = role === "Auditor";
  const [tiQuestBal, setTiQuestBal] = useState(0);
  const [openEditDrawer, setOpenEditDrawer] = useState(false);
  const [showClearFilters, setShowClearFilters] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [filteredTransactionIds, setFilteredTransactionIds] = useState<number[]>([]);

  const [transactionResponse, setTransactionResponse] = useState<
    TransactionDataType[]
  >([]);
  const [transactionFlatResponse, setTransactionFlatResponse] = useState<TransactionDetailsType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  const calculateTiQuestBal = () => {
    getAccountById(1)
      .then((res: AccountType) => {
        setTiQuestBal(res.balance);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getFilteredTransactions = (transactions: TransactionDetailsType[]) => {
    if(transactions?.length > 0 && (!!client || !!account || !!distributionId)) {
      return transactions.filter((transaction) => {
        if(client && account) {
          return transaction.clientId === parseInt(client) && transaction.account === parseInt(account);
        } else if(client) {
          return transaction.clientId === parseInt(client);
        } else if(account) {
          return transaction.account === parseInt(account);
        } else if(distributionId) {
          // If distributionId is provided, the API should already filter the results
          // This is just a fallback in case we need client-side filtering
          return true;
        }
        return false;
      });
    }
    return null;
  };

  const clearFilters = () => {
    // Create new URLSearchParams but preserve distributionId if it exists
    const newParams = new URLSearchParams();
    if (distributionId) {
      newParams.set("distributionId", distributionId);
    }
    setSearchParams(newParams);
    setShowClearFilters(false);
    setFilterModel({ items: [] });
    dispatch(setTransactionsDetailsState(transactionFlatResponse));
  };

  const getAllTransactions = () => {
    setIsLoading(true);
    calculateTiQuestBal();
    getTransactions(startFilterDate, endFilterDate, undefined, distributionId || undefined)
      .then((res) => {
        setTransactionResponse(res);
        const transactions = getTransactionsFromData(res);
        setTransactionFlatResponse(transactions);
        const filteredTransactions = getFilteredTransactions(transactions) || transactions;
        dispatch(setTransactionsDetailsState(filteredTransactions));
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if(transactionFlatResponse?.length > 0 && (!!client || !!account || !!distributionId)) {
      const filteredTransactions = getFilteredTransactions(transactionFlatResponse);
      if(filteredTransactions) {
        dispatch(setTransactionsDetailsState(filteredTransactions));
      }
    }
  }, [client, account, distributionId]);

  useEffect(() => {
    dispatch(updateTimeframe("0"));
  }, []);

  useEffect(() => {
    return () => {
      dispatch(updateTimeframe("0"));
    };
  }, []);

  useEffect(() => {
    if (startFilterDate && endFilterDate) {
      getAllTransactions();
    }
  }, [startFilterDate, endFilterDate]);

  return (
    <>
      <Toolbar
        sx={{
          justifyContent: "space-between",
          margin: "0px 15px",
        }}
      >
        <Typography className="transactions-history-title">
          Transactions History
        </Typography>
        {!isLoading ? (
          <span className="transactions-history-balance">
            TI Quest Balance :{" "}
            <span style={{ fontSize: "18px", fontWeight: 600 }}>
              {`${formatDollar(tiQuestBal)}`}
            </span>
          </span>
        ) : (
          <Skeleton
            animation="wave"
            variant="text"
            width="250px"
            height="30px"
          />
        )}
        {!auditorView && (
          <TransactionDrawer
            openEditDrawer={openEditDrawer}
            setOpenEditDrawer={setOpenEditDrawer}
            getAllTransactions={getAllTransactions}
            transactionResponse={transactionResponse}
          />
        )}
      </Toolbar>
      <Box sx={{ margin: "20px 40px" }}>
        <div className="data-table">
          {!distributionId && (
            <TableFilters
              showClearFilters={!!client || !!account || !!distributionId || showClearFilters}
              clearFilters={clearFilters}
              transactionIds={filteredTransactionIds}
            />
          )}
          {isLoading ? (
            <TableRowsLoader rowsNum={10} />
          ) : transactionDetails.length > 0 ? (
            <TransactionTable
              transactionData={transactionDetails}
              setOpenEditDrawer={setOpenEditDrawer}
              auditorView={auditorView}
              setShowClearFilters={setShowClearFilters}
              filterModel={filterModel}
              setFilterModel={setFilterModel}
              onFilteredTransactionIdsChange={setFilteredTransactionIds}
            />
          ) : (
            <Typography>No transaction records found</Typography>
          )}
        </div>
      </Box>
    </>
  );
};

export default TransactionsHistory;
